import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  CalendarEventResponse,
  OrganizationMember,
  RecurrenceEditScope,
} from "../../../../types/api";
import {
  directionsUrl,
  getEvent,
  getOrganizationMembers,
  removeMeFromEvent,
  setEventAssignees,
  updateEvent,
  deleteEvent,
} from "../../../../services/calendar";
import {
  CoachSelectField,
  LocationField,
  DateTimeEditor,
} from "./ScheduleFields";
import { useDismiss } from "./useDismiss";
import { Avatar, PlusIcon } from "./CoachAvatar";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getWeather,
  describeWeatherCode,
  type WeatherResult,
} from "../../../../services/weather";
import {
  utcDateInputValue,
  exclusiveEndToInclusiveDate,
} from "../../../../utils/calendarTime";
import { TournamentGamesPanel } from "./TournamentGamesPanel";
import { EventDistributionPanel } from "./EventDistributionPanel";
import { CatalogPublicationToggle } from "./CatalogPublicationToggle";
import { RequestItineraryFromEvent } from "./RequestItineraryFromEvent";
import { ScheduleUpdateBanner } from "./ScheduleUpdateBanner";
import { eventTypeLabelWithEmoji } from "./eventTypeLabel";
import { RecurrenceScopeModal } from "./RecurrenceScopeModal";
import {
  eventNeedsSetup,
  itinerarySetupUrl,
  hasAutolaunched,
  markAutolaunched,
} from "./itinerarySetup";
import { eventGameCoaches, formatGameCount } from "./gameCoaches";
import "./ScheduleSection.css";

interface EventViewPanelProps {
  event: CalendarEventResponse;
  /** Open the editor. `focusLocation` jumps to + focuses the location field. */
  onEdit: (focusLocation?: boolean) => void;
  /** Open the read-only itinerary view for this tournament (set-up tournaments only). */
  onOpenItinerary?: () => void;
  onClose: () => void;
  /** Notify the parent (schedule/calendar) that this event's data changed (e.g. an
   *  assignment) so it can refetch and reflect it without a manual page refresh. */
  onChanged?: () => void;
}

/** Read-only "view event" panel — distinct from the editor. Leads with the venue + live weather. */
export const EventViewPanel = ({ event, onEdit, onOpenItinerary, onClose, onChanged }: EventViewPanelProps) => {
  const { user, isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const currentUserId = user?.profile?.id;

  // A distributed copy the recipient hasn't set up yet: they still need to match
  // teams, follow players, review, and assign via the Build Itinerary wizard.
  const needsSetup = event.eventType === "Tournament" && eventNeedsSetup(event);
  const openSetupWizard = () => navigate(itinerarySetupUrl(event.id));

  // Auto-launch the wizard the FIRST time a recipient opens a needs-setup copy.
  // A sessionStorage flag keyed by event id guards the loop: after they cancel
  // out (which returns to /schedule) and reopen the event, we don't auto-launch
  // again — the persistent CTA below covers that. The mounted flag keeps a late
  // navigate() from firing into an unmounted panel.
  const autoLaunchedRef = useRef(false);
  useEffect(() => {
    if (!needsSetup) return;
    if (autoLaunchedRef.current) return;
    if (hasAutolaunched(event.id)) return;
    autoLaunchedRef.current = true;
    markAutolaunched(event.id);
    let active = true;
    // Defer a tick so the panel finishes mounting before we route away; the
    // mounted flag cancels the navigate if the panel closes first.
    const id = window.setTimeout(() => {
      if (active) navigate(itinerarySetupUrl(event.id));
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [needsSetup, event.id, navigate]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);

  // Event-level assignees (tournament coaches), edited inline with the avatar + "+"
  // pattern. Local state so the panel reflects changes without a full refetch.
  const [assignees, setAssignees] = useState(event.assignees);
  // Coaches on some of the games but not the tournament itself (e.g. added by the Build
  // Itinerary wizard). Shown under the event coaches so they don't look unassigned.
  const [gameCoaches, setGameCoaches] = useState(() => eventGameCoaches(event));
  const [pickerOpen, setPickerOpen] = useState(false);
  const assignRef = useDismiss<HTMLDivElement>(pickerOpen, () => setPickerOpen(false));
  const [datesEditing, setDatesEditing] = useState(false);
  const datesRef = useDismiss<HTMLDivElement>(datesEditing, () => setDatesEditing(false));
  const [assignMeError, setAssignMeError] = useState("");
  // Bump to refetch the games panel below after an event-level assignee change
  // (it alters every game's effective coverage / "You're covering").
  const [gamesReload, setGamesReload] = useState(0);
  // Local copy so inline field edits (title, notes) show immediately and later
  // saves build on the latest values, not the original prop.
  const [ev, setEv] = useState(event);
  const [fieldError, setFieldError] = useState("");

  // Save one or more inline-edited fields via the full-replace update endpoint,
  // preserving everything else (assignees omitted => left unchanged). Optimistic.
  const saveField = async (patch: Partial<CalendarEventResponse>) => {
    const next = { ...ev, ...patch };
    setEv(next);
    setFieldError("");
    try {
      const updated = await updateEvent(event.id, {
        title: next.title,
        eventType: next.eventType,
        startsAtUtc: next.startsAtUtc,
        endsAtUtc: next.endsAtUtc,
        isAllDay: next.isAllDay,
        timeZone: next.timeZone,
        locationName: next.locationName,
        locationAddress: next.locationAddress,
        locationLat: next.locationLat,
        locationLng: next.locationLng,
        locationPlaceId: next.locationPlaceId,
        itineraryUrl: next.itineraryUrl,
        notes: next.notes,
      });
      setEv(updated);
      onChanged?.();
    } catch (err) {
      setEv(ev);
      setFieldError(getSafeErrorMessage(err, "Couldn't save your change. Try again."));
    }
  };
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const selectedIds = assignees.map((a) => a.userId);
  const alreadyAssigned =
    currentUserId != null && selectedIds.includes(currentUserId);
  const onSomeGames =
    currentUserId != null && gameCoaches.some((c) => c.userId === currentUserId);
  // Covering = on the tournament itself OR on any of its games.
  const covering = alreadyAssigned || onSomeGames;
  const visibleGameCoaches = gameCoaches.filter((c) => !selectedIds.includes(c.userId));

  // Replace the event's assignees. Optimistic, reverts on failure, and refreshes
  // the calendar so avatars / "My events" update without a manual reload.
  const applyAssignees = async (next: string[]) => {
    const prev = assignees;
    setAssignees(
      next.map((id) => {
        const cur = prev.find((a) => a.userId === id);
        if (cur) return cur;
        const m = membersById.get(id);
        return { userId: id, initials: m?.initials ?? "?", displayName: m?.displayName ?? "Coach" };
      }),
    );
    setAssignMeError("");
    try {
      const updated = await setEventAssignees(event.id, next);
      setAssignees(updated.assignees);
      setGameCoaches(eventGameCoaches(updated));
      onChanged?.();
      // The games below inherit event-level coverage; refetch so they reflect it.
      setGamesReload((n) => n + 1);
    } catch (err) {
      setAssignees(prev);
      setAssignMeError(getSafeErrorMessage(err, "Couldn't update the assigned coaches."));
    }
  };

  const handleAssignMe = () => {
    if (alreadyAssigned || currentUserId == null) return;
    void applyAssignees([...selectedIds, currentUserId]);
  };

  const [confirmingRemoveMe, setConfirmingRemoveMe] = useState(false);
  const [removingMe, setRemovingMe] = useState(false);

  // Take the current coach off the tournament and all of its games in one call, then refetch
  // the event so the lists match the server (the endpoint returns no body).
  const confirmRemoveMe = async () => {
    if (currentUserId == null) return;
    setRemovingMe(true);
    setAssignMeError("");
    try {
      await removeMeFromEvent(event.id);
    } catch (err) {
      setRemovingMe(false);
      setConfirmingRemoveMe(false);
      setAssignMeError(getSafeErrorMessage(err, "Couldn't remove you. Try again."));
      return;
    }
    // The removal succeeded, so reflect it right away even if the refetch below fails.
    setAssignees((prev) => prev.filter((a) => a.userId !== currentUserId));
    setGameCoaches((prev) => prev.filter((c) => c.userId !== currentUserId));
    setRemovingMe(false);
    setConfirmingRemoveMe(false);
    onChanged?.();
    setGamesReload((n) => n + 1);
    try {
      const fresh = await getEvent(event.id);
      setAssignees(fresh.assignees);
      setGameCoaches(eventGameCoaches(fresh));
    } catch {
      /* non-fatal: local state already reflects the removal */
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // One occurrence of a repeating event gets the "which occurrences?" prompt instead of the plain
  // confirm, the same as the editor panel, so a coach can remove the whole series from here.
  const isSeries = event.recurrenceId != null;

  const confirmDelete = async (scope?: RecurrenceEditScope) => {
    setDeleting(true);
    try {
      await deleteEvent(event.id, scope);
      setConfirmingDelete(false);
      onChanged?.();
      onClose();
    } catch (err) {
      setConfirmingDelete(false);
      setFieldError(getSafeErrorMessage(err, "Couldn't delete this event. Try again."));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    let active = true;
    getOrganizationMembers()
      .then((m) => {
        if (active) setMembers(m);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      active = false;
    };
  }, [event.eventType]);

  const dir = directionsUrl({
    locationPlaceId: ev.locationPlaceId,
    locationLat: ev.locationLat,
    locationLng: ev.locationLng,
    locationAddress: ev.locationAddress,
    locationName: ev.locationName,
  });

  const itineraryUrl = (ev.itineraryUrl ?? "").trim();
  // "Verified" = resolved to a real place (has coords), which is what directions + weather need.
  const locationVerified = ev.locationLat != null && ev.locationLng != null;

  return (
    <aside className="event-editor event-view" aria-label="View event">
      <div className="event-editor__head">
        <div className="event-editor__title">View event</div>
        <ModalCloseButton onClick={onClose} />
      </div>

      <div className="event-editor__body">
        {event.eventType === "Tournament" && (
          <ScheduleUpdateBanner calendarEventId={event.id} />
        )}
        {needsSetup && (
          <div className="itinerary-setup-cta" data-testid="view-setup-cta">
            <div className="itinerary-setup-cta__body">
              <div className="itinerary-setup-cta__title">Set up your itinerary</div>
              <div className="itinerary-setup-cta__msg">
                Someone shared this tournament schedule with your org. Match its
                teams to your teams, follow the players you're watching, review the
                games, and assign your coaches to finish setting it up.
              </div>
            </div>
            <Button variant="primary"
              type="button"
              className="itinerary-setup-cta__btn"
              onClick={openSetupWizard}
              data-testid="view-setup-button"
            >
              Start setup
            </Button>
          </div>
        )}
        <div className="event-view__type">{eventTypeLabelWithEmoji(event.eventType)}</div>
        <h2 className="event-view__title">
          <InlineEdit
            value={ev.title}
            onSave={(t) => saveField({ title: t })}
            placeholder="Untitled event"
            ariaLabel="Edit event title"
          />
        </h2>
        {datesEditing ? (
          <div ref={datesRef}>
            <DateTimeEditor
              event={ev}
              onSave={(patch) => {
                void saveField(patch);
                setDatesEditing(false);
              }}
              onCancel={() => setDatesEditing(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            className="event-view__dates inline-edit"
            onClick={() => setDatesEditing(true)}
            title="Click to edit dates and time"
            data-testid="view-dates"
          >
            {formatDateRange(ev)}
            {!ev.isAllDay && <span className="event-view__dates-time"> · timed</span>}
          </button>
        )}

        <div className="event-view__section">
          <div className="event-view__label">Location</div>
          <div className="event-view__loc-name">
            <LocationField
              name={ev.locationName || ev.locationAddress || ""}
              onPick={(loc) =>
                saveField({
                  locationName: loc.name,
                  locationAddress: loc.address,
                  locationLat: loc.lat,
                  locationLng: loc.lng,
                  locationPlaceId: loc.placeId,
                })
              }
            />
          </div>
          {ev.locationAddress && ev.locationName && (
            <div className="event-view__loc-addr">{ev.locationAddress}</div>
          )}
          {locationVerified ? (
            dir ? (
              <a
                className="location-link"
                href={dir}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-directions"
              >
                ↗ Directions
              </a>
            ) : null
          ) : (
            <div className="event-view__loc-warn" data-testid="view-location-unverified">
              <div className="event-view__loc-warn-msg">
                {ev.locationName ? (
                  <>
                    We couldn't verify <b>{ev.locationName}</b> on the map. Click it above to pick a
                    verified place so directions and weather work.
                  </>
                ) : (
                  <>No location set. Click above to add one.</>
                )}
              </div>
            </div>
          )}
        </div>

        <WeatherPanel event={ev} />

        <div className="event-view__section">
          <div className="event-view__label event-view__label--row">
            <span>Assigned coaches</span>
            {covering && (
              <span className="game-row__mine-badge" data-testid="view-covering-badge">
                You're covering
              </span>
            )}
          </div>
          {/* Jira-style: avatars with hover-remove + a "+" that opens the coach list. */}
          <div className="sf-assign event-view__assignees" ref={assignRef}>
            {/* Removal is done by unchecking in the "+" coach list. */}
            {assignees.map((a) => (
              <Avatar
                key={a.userId}
                id={a.userId}
                initials={a.initials}
                ring={a.userId === currentUserId}
                title={a.displayName}
              />
            ))}
            <button
              type="button"
              className={`sf-avatar sf-assign__add${pickerOpen ? " sf-assign__add--on" : ""}`}
              onClick={() => setPickerOpen((v) => !v)}
              aria-expanded={pickerOpen}
              aria-label="Add a coach"
              title="Add a coach"
              data-testid="view-add-coach"
            >
              <PlusIcon />
            </button>
            {pickerOpen && (
              <CoachSelectField
                members={members}
                value={selectedIds}
                onChange={applyAssignees}
                currentUserId={currentUserId}
                menuOnly
              />
            )}
          </div>
          {visibleGameCoaches.length > 0 && (
            <div className="event-view__game-coaches" data-testid="view-game-coaches">
              <div className="event-editor__hint">On some games</div>
              <ul className="event-view__game-coach-list">
                {visibleGameCoaches.map((c) => (
                  <li key={c.userId} className="event-view__game-coach">
                    <Avatar
                      id={c.userId}
                      initials={c.initials}
                      ring={c.userId === currentUserId}
                      title={c.displayName}
                    />
                    <span className="event-view__game-coach-name">{c.displayName}</span>
                    <span className="event-editor__hint event-view__game-coach-count">
                      {formatGameCount(c.gameCount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {assignees.length === 0 && visibleGameCoaches.length === 0 && (
            <div className="event-editor__hint">Nobody assigned yet.</div>
          )}
          {covering ? (
            <Button variant="secondary"
              type="button"
              className="event-view__assign-me"
              onClick={() => setConfirmingRemoveMe(true)}
              data-testid="view-remove-me"
            >
              Remove me
            </Button>
          ) : (
            <Button variant="secondary"
              type="button"
              className="event-view__assign-me"
              onClick={handleAssignMe}
              data-testid="view-assign-me"
            >
              Assign me
            </Button>
          )}
          {assignMeError && (
            <div className="event-editor__error" role="alert">
              {assignMeError}
            </div>
          )}
        </div>

        {itineraryUrl && (
          <div className="event-view__section">
            <div className="event-view__label">Itinerary link</div>
            {/^https?:\/\//i.test(itineraryUrl) ? (
              <a
                className="location-link"
                href={itineraryUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-itinerary-link"
              >
                ↗ {itineraryUrl}
              </a>
            ) : (
              <div className="event-view__loc-addr">{itineraryUrl}</div>
            )}
          </div>
        )}

        <div className="event-view__section">
          <div className="event-view__label">Notes</div>
          <InlineEdit
            value={ev.notes ?? ""}
            onSave={(n) => saveField({ notes: n })}
            multiline
            placeholder="Add notes"
            displayClassName="event-view__notes"
            ariaLabel="Edit notes"
          />
        </div>
        {fieldError && (
          <div className="event-editor__error" role="alert">
            {fieldError}
          </div>
        )}


        {/* REQ-04: raise an itinerary request straight off the tournament that is already on the
            calendar. The request carries only the event id, so nothing here re-asks for a name or
            dates the calendar already holds. */}
        {event.eventType === "Tournament" && (
          <RequestItineraryFromEvent
            event={{
              id: event.id,
              title: ev.title,
              startsAtUtc: ev.startsAtUtc,
              endsAtUtc: ev.endsAtUtc,
              isAllDay: ev.isAllDay,
            }}
          />
        )}

        {event.eventType === "Tournament" && (
          <TournamentGamesPanel
            calendarEventId={event.id}
            members={members}
            currentUserId={currentUserId}
            onChanged={onChanged}
            reloadToken={gamesReload}
          />
        )}

        {isSystemAdmin && (
          <>
            {/* Publishing is what fills the shared catalog; distribution is the direct push. Both
                are system_admin-only and belong together. */}
            <CatalogPublicationToggle event={event} onChanged={() => onChanged?.()} />
            <EventDistributionPanel eventId={event.id} />
          </>
        )}
      </div>

      <div className="event-editor__foot">
        <Button variant="secondary"
          type="button"
          className="event-editor__btn--danger"
          onClick={() => setConfirmingDelete(true)}
          data-testid="view-delete"
        >
          Delete
        </Button>
        <div className="event-editor__foot-right">
          <Button variant="secondary"  onClick={onClose}>
            Close
          </Button>
          {onOpenItinerary && event.eventType === "Tournament" && !needsSetup && (
            <Button variant="secondary"
              type="button"
              onClick={onOpenItinerary}
              data-testid="open-itinerary"
              title="See your games for this tournament"
            >
              Open itinerary
            </Button>
          )}
          {event.eventType === "Tournament" ? (
            // Title/dates/location/notes/coaches are edited inline above, and
            // Edit itinerary carries match/follow/review/assign + re-import, so the
            // raw "Event details" editor is no longer needed for tournaments.
            <Button variant="primary"
              type="button"
              onClick={openSetupWizard}
              data-testid="view-edit-itinerary"
              title="Match teams, follow players, review games, assign staff, and re-import"
            >
              Edit itinerary
            </Button>
          ) : (
            <Button variant="primary"
              onClick={() => onEdit()}
              data-testid="view-edit-button"
            >
              Edit event
            </Button>
          )}
        </div>
      </div>

      <RecurrenceScopeModal
        isOpen={isSeries && confirmingDelete}
        action="delete"
        eventId={event.id}
        busy={deleting}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={(scope) => void confirmDelete(scope)}
      />

      <ConfirmModal
        isOpen={confirmingRemoveMe}
        onClose={() => setConfirmingRemoveMe(false)}
        onConfirm={() => void confirmRemoveMe()}
        title="Remove me"
        message="Remove yourself from this tournament? You'll come off the tournament and all of its games."
        confirmText="Remove me"
        cancelText="Cancel"
        busy={removingMe}
      />

      <ConfirmModal
        isOpen={!isSeries && confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => void confirmDelete()}
        title="Delete event"
        message={`Delete "${ev.title || "this event"}"? This can't be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </aside>
  );
};

/**
 * Click-to-edit text: renders as plain text (with a subtle hover affordance) until
 * clicked, then becomes an input/textarea. Enter (single-line) or blur commits;
 * Escape cancels. Inherits the surrounding font so the title reads as the title.
 */
function InlineEdit({
  value,
  onSave,
  multiline = false,
  placeholder = "Click to add",
  displayClassName = "",
  ariaLabel,
}: {
  value: string;
  onSave: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  displayClassName?: string;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const v = draft.trim();
    if (v !== value.trim()) onSave(v);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        className={`inline-edit ${displayClassName}`}
        onClick={() => setEditing(true)}
        aria-label={ariaLabel ?? "Click to edit"}
        title="Click to edit"
      >
        {value ? value : <span className="inline-edit__ph">{placeholder}</span>}
      </button>
    );
  }
  if (multiline) {
    return (
      <div className="inline-edit__editor">
        <textarea
          autoFocus
          className={`inline-edit__input inline-edit__input--notes ${displayClassName}`}
          value={draft}
          rows={4}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancel();
          }}
        />
        <div className="inline-edit__actions">
          <Button variant="primary"
            type="button"
            className="inline-edit__save"
            onClick={commit}
          >
            Save
          </Button>
          <Button variant="secondary"
            type="button"
            className="inline-edit__cancel"
            onClick={cancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  return (
    <input
      autoFocus
      className={`inline-edit__input ${displayClassName}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
        if (e.key === "Escape") cancel();
      }}
    />
  );
}

/** Current conditions + a daily forecast for the event's days that fall in range. */
function WeatherPanel({ event }: { event: CalendarEventResponse }) {
  const hasCoords = event.locationLat != null && event.locationLng != null;
  const [state, setState] = useState<
    { status: "idle" | "loading" | "error" } | { status: "ready"; data: WeatherResult }
  >({ status: hasCoords ? "loading" : "idle" });

  useEffect(() => {
    if (!hasCoords) {
      setState({ status: "idle" });
      return;
    }
    let active = true;
    setState({ status: "loading" });
    const startDate = utcDateInputValue(event.startsAtUtc);
    const endDate = event.isAllDay
      ? exclusiveEndToInclusiveDate(event.endsAtUtc)
      : utcDateInputValue(event.endsAtUtc);
    getWeather(event.locationLat as number, event.locationLng as number, startDate, endDate)
      .then((data) => {
        if (active) setState({ status: "ready", data });
      })
      .catch(() => {
        if (active) setState({ status: "error" });
      });
    return () => {
      active = false;
    };
  }, [hasCoords, event.locationLat, event.locationLng, event.startsAtUtc, event.endsAtUtc, event.isAllDay]);

  if (!hasCoords) {
    return (
      <div className="event-view__section" data-testid="view-weather">
        <div className="event-view__label">Weather</div>
        <div className="event-editor__hint">
          Pin a verified location (with an address) to see current conditions and the forecast.
        </div>
      </div>
    );
  }

  // Coords without a resolved Google place id came from free text, not a confirmed venue, so the
  // forecast could be for the wrong spot. Flag it rather than presenting it as certain.
  const unverifiedPlace = event.locationPlaceId == null;

  // Rough day count for the loading skeleton so it reserves ~the same height as the
  // loaded forecast and the panel doesn't jump when weather arrives.
  const startKey = utcDateInputValue(event.startsAtUtc);
  const endKey = event.isAllDay
    ? exclusiveEndToInclusiveDate(event.endsAtUtc)
    : utcDateInputValue(event.endsAtUtc);
  const spanDays = Math.round(
    (new Date(`${endKey}T12:00:00Z`).getTime() - new Date(`${startKey}T12:00:00Z`).getTime()) /
      86_400_000,
  );
  const skeletonDays = Math.min(7, Math.max(1, (Number.isFinite(spanDays) ? spanDays : 0) + 1));

  return (
    <div className="event-view__section" data-testid="view-weather">
      <div className="event-view__label">Weather</div>
      {unverifiedPlace && (
        <div className="event-editor__hint" data-testid="view-weather-unverified">
          This location isn't a verified place, so the forecast may be for the wrong spot. Set a
          verified location to be sure.
        </div>
      )}
      {state.status === "loading" && (
        <div className="wx wx--skeleton" data-testid="view-weather-loading" aria-hidden="true">
          <div className="wx-skel wx-skel__current" />
          <div className="wx-days">
            {Array.from({ length: skeletonDays }).map((_, i) => (
              <div key={i} className="wx-day wx-skel wx-skel__day" />
            ))}
          </div>
        </div>
      )}
      {state.status === "error" && (
        <div className="event-editor__hint">Weather is unavailable right now.</div>
      )}
      {state.status === "ready" && (
        <div className="wx">
          {state.data.current && (
            <div className="wx-current" data-testid="view-weather-current">
              <span className="wx-current__temp">{state.data.current.temperatureF}°F</span>
              <div className="wx-current__info">
                <span className="wx-current__desc">
                  {state.data.current.description} · wind {state.data.current.windMph} mph
                </span>
                <span className="wx-current__tag">now at the venue</span>
              </div>
            </div>
          )}
          {state.data.daily.length > 0 && (
            <div className="wx-days">
              {state.data.daily.map((d) => (
                <div key={d.date} className="wx-day">
                  <div className="wx-day__date">{formatShortDay(d.date)}</div>
                  <div className="wx-day__hi">{d.highF}°</div>
                  <div className="wx-day__lo">{d.lowF}°</div>
                  <div className="wx-day__desc">{describeWeatherCode(d.code)}</div>
                  {d.precipChance != null && (
                    <div className="wx-day__precip">💧 {d.precipChance}%</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {state.data.daily.length === 0 && state.data.forecastBeyondRange && (
            <div className="event-editor__hint">
              The forecast for these dates isn't available yet — check back closer to the event.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** "Jul 16, 2026" or "Jul 16 – 19, 2026"-ish range from the event's stored instants. */
function formatDateRange(event: CalendarEventResponse): string {
  const startKey = utcDateInputValue(event.startsAtUtc);
  const endKey = event.isAllDay
    ? exclusiveEndToInclusiveDate(event.endsAtUtc)
    : utcDateInputValue(event.endsAtUtc);
  const start = formatLongDay(startKey);
  if (startKey === endKey) return start;
  return `${start} – ${formatLongDay(endKey)}`;
}

function formatLongDay(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

function formatShortDay(dayKey: string): string {
  const d = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}
